/**
 * The rail prices in minor units and the currencies it settles in all carry two
 * decimals. Kept as one named conversion so no component divides by hand.
 */
export const minorUnitsToMajor = (minorUnits: number): number => minorUnits / 100;

/**
 * One payment-rail amount as the brand reads it, in the language it browses in.
 *
 * THE RULE, one for every surface of the rail: an amount that will be, is being
 * or has been debited always carries its cents. "848 €" in the table beside
 * "848,00 €" in the drawer reads as two different amounts, and on money the
 * brand is about to lose that doubt costs more than the two characters saved.
 * `Intl` writes both decimals by default, so the rule is simply not to opt out
 * of them.
 *
 * `formatCents` (`brief-templates/_shared/format-money`) keeps dropping the
 * decimals of a round amount, and stays the formatter for everything outside
 * the rail — brief compensation, gifted-product values — where the figure is a
 * term being discussed, not a debit being authorised.
 *
 * Returned as a string rather than a `<Money>` node because most of these
 * amounts are interpolated inside a sentence, not laid out in a column.
 *
 * The collab journal reads its currency out of an untyped event payload, so a
 * code `Intl` refuses must cost one plain-looking amount rather than the whole
 * panel it is rendered in. The fallback still writes both decimals: degrading
 * is not a reason to break the rule above.
 */
export const formatSettlementAmount = (
  minorUnits: number,
  currency: string,
  language: string
): string => {
  const amount = minorUnitsToMajor(minorUnits);

  try {
    return new Intl.NumberFormat(language, { style: 'currency', currency }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency}`;
  }
};

/**
 * A quote rate, in percent, ready to be dropped into a label.
 *
 * The rate is read from the frozen quote and never derived from the amounts:
 * a client-side division would be a rounded guess, while this is the rate the
 * brand was actually priced at. Null when the server sent none, so the caller
 * names the fee without naming a rate rather than printing a wrong number.
 */
export const formatQuoteRate = (
  basisPoints: number | null | undefined,
  language: string
): string | null => {
  if (typeof basisPoints !== 'number' || !Number.isFinite(basisPoints)) return null;

  return new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(basisPoints / 100);
};
